/**
 * Formats a millisecond offset as a zero-padded `mm:ss` elapsed-time label —
 * the compact marker shown next to a speaker turn (Enhanced-mode timeline
 * cards, live Input-column speaker labels).
 *
 * @param value Milliseconds elapsed since recording start.
 * @returns A zero-padded `mm:ss` label.
 */
export function formatElapsedTime(value: number): string {
  const totalSeconds = Math.max(0, Math.floor(value / 1000))
  const minutes = Math.floor(totalSeconds / 60)
  const seconds = totalSeconds % 60
  return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`
}

/**
 * Formats a turn's span as `mm:ss – mm:ss` so a reader can tell how long the
 * turn ran, not just when it started.
 *
 * Falls back to the bare start label when the end is unknown (`end_time` is
 * optional on the STT events) or when both offsets floor to the same second —
 * a sub-second turn would otherwise print the identical label twice.
 *
 * @param startMs Milliseconds elapsed since recording start.
 * @param endMs Milliseconds elapsed at the end of the turn, when known.
 * @returns Either `mm:ss – mm:ss` or a single `mm:ss` label.
 * @example
 * formatElapsedRange(10_620, 14_000) // '00:10 – 00:14'
 * formatElapsedRange(10_620, 10_680) // '00:10'
 * formatElapsedRange(10_620)         // '00:10'
 */
export function formatElapsedRange(startMs: number, endMs?: number): string {
  const start = formatElapsedTime(startMs)
  if (endMs === undefined) return start
  const end = formatElapsedTime(endMs)
  return end === start ? start : `${start} – ${end}`
}
